import { forwardRef } from "react";
import { cn } from "@/utils";
import type { DropdownOption, DropdownOptionGroup } from "./Dropdown";

interface OptionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  option: DropdownOption;
  flatIndex: number;
  listboxId: string;
  value: string;
  sizeClasses: string;
  highlightedIndex: number;
  onChange: (value: string) => void;
  onClose: () => void;
  renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
}

const OptionButton = ({
  option,
  flatIndex,
  listboxId,
  value,
  sizeClasses,
  highlightedIndex,
  onChange,
  onClose,
  renderOptionPrefix,
}: OptionButtonProps) => (
  <button
    key={option.value}
    id={`${listboxId}-option-${flatIndex}`}
    type="button"
    role="option"
    aria-selected={option.value === value}
    onClick={() => {
      onChange(option.value);
      onClose();
    }}
    className={cn(
      "w-full text-left hover:bg-bg-secondary flex items-center gap-2",
      sizeClasses,
      option.value === value
        ? "bg-bg-secondary text-text-primary font-medium"
        : "text-text-secondary",
      highlightedIndex === flatIndex && "bg-bg-tertiary",
    )}
  >
    {renderOptionPrefix?.(option)}
    {option.label}
  </button>
);

interface OptionsListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  groups?: DropdownOptionGroup[];
  options: DropdownOption[];
  listboxId: string;
  value: string;
  sizeClasses: string;
  highlightedIndex: number;
  onChange: (value: string) => void;
  onClose: () => void;
  renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
}

export const OptionsList = forwardRef<HTMLDivElement, OptionsListProps>(function OptionsList(
  {
    groups,
    options,
    listboxId,
    value,
    sizeClasses,
    highlightedIndex,
    onChange,
    onClose,
    renderOptionPrefix,
    className,
    ...props
  },
  ref,
) {
  if (groups) {
    let flatIndex = 0;
    return (
      <div ref={ref} className={className} {...props}>
        {groups.map((group) => (
          <div key={group.label}>
            <div className="px-2 py-1 text-2xs font-medium text-text-tertiary bg-bg-secondary">
              {group.label}
            </div>
            {group.options.map((option) => {
              const idx = flatIndex++;
              return (
                <OptionButton
                  key={option.value}
                  option={option}
                  flatIndex={idx}
                  listboxId={listboxId}
                  value={value}
                  sizeClasses={sizeClasses}
                  highlightedIndex={highlightedIndex}
                  onChange={onChange}
                  onClose={onClose}
                  renderOptionPrefix={renderOptionPrefix}
                />
              );
            })}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div ref={ref} className={className} {...props}>
      {options.map((option, index) => (
        <OptionButton
          key={option.value}
          option={option}
          flatIndex={index}
          listboxId={listboxId}
          value={value}
          sizeClasses={sizeClasses}
          highlightedIndex={highlightedIndex}
          onChange={onChange}
          onClose={onClose}
          renderOptionPrefix={renderOptionPrefix}
        />
      ))}
    </div>
  );
});
